import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Plus } from 'lucide-react';
import { useGroupsManagement } from '@/hooks/useGroupsManagement';
import { CreateGroupDialog } from './CreateGroupDialog';
import { GroupCard } from './GroupCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentPermissions } from '@/hooks/useDocumentPermissions';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function GroupsManagementCard() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { canCreateGroups, maxGroups } = useDocumentPermissions();
  const { groups, isLoading } = useGroupsManagement();

  const currentGroupCount = groups?.length || 0;
  const canAddMore = maxGroups === -1 || currentGroupCount < maxGroups;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Grupos de Compartilhamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!canCreateGroups) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Grupos de Compartilhamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              🔒 Grupos de compartilhamento disponíveis apenas nos planos <strong>Pro</strong> e <strong>Premium</strong>.
              <br />
              <a href="/planos" className="text-primary underline mt-2 inline-block">
                Ver planos disponíveis →
              </a>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Grupos de Compartilhamento
            </CardTitle>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              disabled={!canAddMore}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar Grupo
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {maxGroups === -1
              ? `✅ ${currentGroupCount} grupos criados (Ilimitado no plano Premium)`
              : `📊 ${currentGroupCount}/${maxGroups} grupos`}
          </p>
        </CardHeader>
        <CardContent>
          {!groups || groups.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum grupo criado ainda. Crie seu primeiro grupo!
            </p>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <GroupCard key={group.id} group={group} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateGroupDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  );
}
